/**
 * Standard terms per vessel class, used to prefill the Create New Cargo form.
 *
 * Vessel class is readable but not writable. The `TIXCargoes` view exposes it as
 * `Segment` (see `CARGO_SEGMENTS` below), but the write path has nowhere to put
 * it: no vessel-class column on `tblCargoes`, none in `CreateCargoListRequest`,
 * and none among the 34 parameters of `proctblCargoInsert`. So the quick-select
 * below stays UI-only state that seeds these four inputs and is never sent.
 *
 * That is also why this is a string union rather than one of the numeric
 * `vesselSizeId` encodings: Fixtures uses 2 = VLCC / 3 = Suezmax while Position
 * List uses 2 = VLCC / 1 = Suezmax (see `utils/fixtureUtils.ts`), and cargo has
 * no id to persist to, so borrowing either would imply an association that does
 * not exist.
 */
export type CargoVesselType = 'VLCC' | 'Suezmax';

export interface CargoVesselTypeDefaults {
  /**
   * String, not number: `initialValues.cargoSize` is `''` and the submit handler
   * is typed `values: typeof initialValues`, so this slot has to stay `string`
   * for `parseFloat(values.cargoSize)` to compile under `strict`.
   */
  cargoSize: string;
  /** Hours. */
  laytime: number;
  /** Percent, as the input displays it - the submit handler divides by 100. */
  primaryComm: number;
  /** Percent, as the input displays it - the submit handler divides by 100. */
  addressComm: number;
}

export const CARGO_VESSEL_TYPE_DEFAULTS: Record<CargoVesselType, CargoVesselTypeDefaults> = {
  VLCC: { cargoSize: '280000', laytime: 120, primaryComm: 1.25, addressComm: 2.5 },
  Suezmax: { cargoSize: '130000', laytime: 84, primaryComm: 1.25, addressComm: 2.5 },
};

export const CARGO_VESSEL_TYPES = Object.keys(
  CARGO_VESSEL_TYPE_DEFAULTS
) as readonly CargoVesselType[];

/**
 * Vessel class as the `TIXCargoes` view reports it, in the `Segment` column.
 * `varchar(20) NOT NULL` on the view, but typed defensively everywhere it is
 * read - an unrecognised or missing value renders nothing rather than throwing.
 *
 * Read-only: see the note on `CargoVesselType` above for why it cannot be saved.
 */
export type CargoSegment = 'VL' | 'SM';

/** Segment code -> the label shown to users. */
export const CARGO_SEGMENT_LABELS: Record<CargoSegment, CargoVesselType> = {
  VL: 'VLCC',
  SM: 'Suezmax',
};

export const CARGO_SEGMENTS = Object.keys(CARGO_SEGMENT_LABELS) as readonly CargoSegment[];

/** List filter value: a segment code, or `all` for no filtering. */
export type CargoSegmentFilter = 'all' | CargoSegment;

export const CARGO_SEGMENT_FILTER_ALL = 'all' satisfies CargoSegmentFilter;

/**
 * Resolve a raw `segment` value to its display label, or `null` when it is
 * missing or not one we know. Callers render nothing for `null` rather than
 * showing a raw code the user has no way to interpret.
 */
export function getCargoSegmentLabel(segment: string | null | undefined): CargoVesselType | null {
  if (!segment) return null;
  return CARGO_SEGMENT_LABELS[segment.trim().toUpperCase() as CargoSegment] ?? null;
}
